import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from 'components/v2/Table'
import { ICreditsField } from 'features/BusinessClients/types'
import { useField } from 'formik'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { DownloadIcon } from 'components/Icons/DownloadIcon'
import { Tag } from 'components/v2/Tag'
import styled from 'styled-components'
import { Row } from 'components/v2/Row'
import { CreditsUploadReceiptModal } from 'features/BusinessClients/components/CreditsUploadReceiptModal'
import { integerToCents } from 'utils/number'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import Pagination from '@10xLabs/react-web/components/Pagination'
import { CancelIcon } from 'components/Icons'
import { isEmpty } from 'lodash'
import { CancelCreditsDepositModal } from 'features/BusinessClients/components/CancelKreditDepositModal'

const StyledTag = styled(Tag)`
  background-color: #f4f0ff;
  border-radius: 8px;
  border: none;
  padding: 8px 16px;
`
const DownloadButton = styled.button`
  text-decoration: none;
  color: #916ee8;
  background-color: transparent;
  border: none;
  cursor: pointer;
`
const Flex = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  gap: 8px;
  color: #916ee8;
  > svg {
    fill: #916ee8;
  }
`
const EmptyBox = styled.div`
  height: 60px;
`

const DeleteMobileKreditsAction = styled.div`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  width: 100%;

  svg {
    fill: #b0b0b0;
    height: 18px;
    width: 18px;
  }
`

const CreditsTable = ({
  deposits,
  showImgColumn,
  showNotesColumn,
  downloadFile,
  onDeletePendingDeposit
}: {
  deposits: ICreditsField[]
  showImgColumn: boolean
  showNotesColumn: boolean
  downloadFile: (file: File) => void
  onDeletePendingDeposit: (index: number) => void
}): ReactElement => {
  const [page, setPage] = useState(1)
  const [transactionToRemoveId, setTransactionToRemoveId] = useState<string | null>(null)
  const itemsPerPage = 20
  const paginatedDeposits = deposits.slice((page - 1) * itemsPerPage, page * itemsPerPage)

  return (
    <>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>
                <FormattedMessage id="newBusinessClientForm.credits.executedBy" defaultMessage="EXECUTED BY" />
              </Th>
              <Th>
                <FormattedMessage id="newBusinessClientForm.credits.date" defaultMessage="DATE" />
              </Th>
              {showImgColumn ? (
                <Th>
                  <FormattedMessage id="newBusinessClientForm.credits.receipt" defaultMessage="RECEIPT" />
                </Th>
              ) : null}
              {showNotesColumn ? (
                <Th>
                  <FormattedMessage id="newBusinessClientForm.credits.notes" defaultMessage="NOTES" />
                </Th>
              ) : null}
              <Th>
                <FormattedMessage id="newBusinessClientForm.credits.amount" defaultMessage="AMOUNT" />
              </Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {paginatedDeposits.map((deposit, index) => (
              <Tr key={index}>
                <Td>
                  {deposit.id ? (
                    deposit.executedBy
                  ) : (
                    <FormattedMessage id="dictionary.pending" defaultMessage="PENDING" />
                  )}
                </Td>
                <Td>{deposit.date.toLocaleDateString()}</Td>
                {showImgColumn ? (
                  <Td>
                    <DownloadButton onClick={(): void => downloadFile(deposit.file as File)}>
                      <StyledTag>
                        <Flex>
                          <span>IMG</span>
                          <DownloadIcon />
                        </Flex>
                      </StyledTag>
                    </DownloadButton>
                  </Td>
                ) : null}
                {showNotesColumn ? <Td>Notes</Td> : null}
                <Td>
                  <FormattedPrice
                    value={deposit.id ? Number(deposit.amount) : integerToCents(deposit.amount)}
                    currency="MXN"
                  />
                </Td>
                <Td>
                  <DeleteMobileKreditsAction
                    onClick={(): void =>
                      deposit.id ? setTransactionToRemoveId(deposit.id) : onDeletePendingDeposit(index)
                    }
                  >
                    <CancelIcon />
                  </DeleteMobileKreditsAction>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <EmptyBox />
      <Pagination.Container>
        <Pagination.Caption
          page={page}
          totalItems={deposits.length}
          currentItemsCount={paginatedDeposits.length}
          limit={itemsPerPage}
          getCaptionMessage={(firstItemIndex: number, lastItemIndex: number): ReactElement => (
            <FormattedMessage
              id="businessClient.credits.pagination.caption"
              defaultMessage="Showing {start}–{end} of {total}"
              values={{
                start: firstItemIndex,
                end: lastItemIndex,
                total: deposits.length
              }}
            />
          )}
        />
        <Pagination.Previous page={page} setPage={setPage} />
        <Pagination.Next page={page} totalItems={deposits.length} limit={itemsPerPage} setPage={setPage} />
      </Pagination.Container>
      {!isEmpty(transactionToRemoveId) && (
        <CancelCreditsDepositModal
          onClose={(): void => setTransactionToRemoveId(null)}
          transactionId={transactionToRemoveId ?? ''}
        />
      )}
    </>
  )
}

export const CreditsFormField = ({ fieldName }: { fieldName: string }): ReactElement => {
  const [field, , helpers] = useField<ICreditsField[]>(fieldName)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const isShowingTable = field.value.length > 0

  const onSubmit = async ({
    filePath,
    file,
    amount
  }: {
    filePath: string
    file: File
    amount: string
  }): Promise<void> => {
    const newValue = [...field.value]
    newValue.unshift({
      amount: amount,
      date: new Date(),
      file: file,
      filePath: filePath
    })
    helpers.setValue(newValue)
  }

  const downloadFile = (file: File): void => {
    if (file) {
      const url = URL.createObjectURL(file)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
  const showImgColumn = false //TODO: Will be implemented in a future Jira ticket
  const showNotesColumn = false //TODO: Will be implemented in a future Jira ticket

  return (
    <>
      <FormLayout.Card>
        <FormLayout.CardHeader>
          <Heading.H4>
            <FormattedMessage id="newBusinessClientForm.credits.title" defaultMessage="Mobile Kredits" />
          </Heading.H4>
          <Button kind="secondary" type="button" onClick={(): void => setIsOpenModal(true)}>
            <FormattedMessage id="newBusinessClientForm.credits.addCredits" defaultMessage="Add Kredits" />
          </Button>
        </FormLayout.CardHeader>
        {isShowingTable ? (
          <Row>
            <Column>
              <CreditsTable
                deposits={field.value}
                showImgColumn={showImgColumn}
                showNotesColumn={showNotesColumn}
                downloadFile={downloadFile}
                onDeletePendingDeposit={(index: number): void => {
                  const newDeposits = [...field.value]
                  newDeposits.splice(index, 1)
                  helpers.setValue(newDeposits)
                }}
              />
            </Column>
          </Row>
        ) : null}
      </FormLayout.Card>
      {isOpenModal && (
        // @ts-expect-error FIXME
        <CreditsUploadReceiptModal setIsOpen={setIsOpenModal} onSubmit={onSubmit} />
      )}
    </>
  )
}
